import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import LaptopAnimation from "./LaptopAnimation";

export default function Hero() {
  return (
    <section
      className="relative pt-32 pb-16 md:pt-40 md:pb-24 min-h-screen flex flex-col items-center justify-start overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-black" />
      <video
        className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-lighten pointer-events-none"
        src="https://customer-assets.emergentagent.com/job_bbade1d5-ca5c-4f1d-bbfd-e47aa63289d4/artifacts/u1vv6t8r_intro%205.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
        data-testid="hero-bg-video"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black" />
      <div className="absolute inset-0 radial-green" />
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="noise-layer" />

      <div className="absolute top-20 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#BEDD7D]/40 to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 border border-[#BEDD7D]/40 bg-[#1A2E1A]/40 backdrop-blur-md rounded-full mb-8"
          data-testid="hero-tagline-pill"
        >
          <ShieldCheck className="w-3.5 h-3.5 text-[#BEDD7D]" />
          <span className="text-xs font-display uppercase tracking-[0.3em] text-[#BEDD7D]">
            Sistema Gamer-Militar de Trading Élite
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="font-display font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.95]"
          data-testid="hero-title"
        >
          Rescata tu
          <br />
          <span className="text-[#BEDD7D] text-glow-mindaro italic">libertad</span>.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-6 max-w-2xl mx-auto font-body text-base md:text-lg text-white/70 leading-relaxed"
          data-testid="hero-subtitle"
        >
          Inicia un viaje transformador junto a traders élite: entre{" "}
          <span className="text-[#BEDD7D] font-medium">rangos, misiones y operaciones</span>{" "}
          con objetivos y recompensas reales en la plataforma de educación de trading más tecnológica del mundo. Honor en la estrategia, gloria en la ejecución.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="https://app.honor.trading/auth/login"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 px-8 py-4 bg-[#BEDD7D] text-black font-display font-bold text-sm uppercase tracking-[0.2em] hover:bg-white hover:shadow-[0_0_40px_rgba(190,221,125,0.6)] transition-all"
            data-testid="hero-cta-ingresar"
          >
            Activa tu misión libertad
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
          <a
            href="#planes"
            className="inline-flex items-center gap-2 px-8 py-4 border border-[#3E4D36] text-white font-display font-bold text-sm uppercase tracking-[0.2em] hover:bg-[#1A2E1A] hover:border-[#BEDD7D]/50 transition-all"
            data-testid="hero-cta-planes"
          >
            Ver packs
          </a>
        </motion.div>

        {/* Social proof row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.0 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs font-display uppercase tracking-[0.2em] text-white/60"
          data-testid="hero-social-proof"
        >
          <span className="flex items-center gap-2">
            <span className="text-[#BEDD7D] text-base font-bold">+6.000</span> traders entrenados
          </span>
          <span className="hidden md:block w-1 h-1 rounded-full bg-[#3E4D36]" />
          <span className="flex items-center gap-2">
            <span className="text-[#BEDD7D] text-base font-bold">4.8★</span> Trustpilot
          </span>
          <span className="hidden md:block w-1 h-1 rounded-full bg-[#3E4D36]" />
          <span className="flex items-center gap-2">
            <span className="text-[#BEDD7D] text-base font-bold">85%</span> win rate señales
          </span>
        </motion.div>
      </div>

      {/* Laptop */}
      <div className="relative z-10 w-full mt-16 md:mt-20 px-4 md:px-8">
        <LaptopAnimation />
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-black pointer-events-none" />
    </section>
  );
}
